import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PROMPT_REPEAT_MIN_SECONDS, useSettings } from '../../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from '../../games/speak';
import { playCorrectPing } from '../../games/ping';
import type { TileMatchConfig } from '../types';

const FEEDBACK_HOLD_MS = 700;

function gridClass(n: number): string {
  if (n <= 4) return 'cols-2';
  if (n <= 9) return 'cols-3';
  return 'cols-4';
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

type Round = { say: string; answer: string; tiles: string[] };

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
  'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];

function inAnswerForm(word: string, answer: string): string {
  const n = NUMBER_WORDS.indexOf(word.toLowerCase());
  if (n >= 0 && /^\d+$/.test(answer.trim())) return String(n);
  return word.toLowerCase() === answer.trim().toLowerCase() ? answer.trim() : word;
}

function namedOptions(say: string, answer: string): string[] {
  const m = /([\p{L}\p{N}'-]+)\s+or\s+([\p{L}\p{N}'-]+)\s*\?\s*$/iu.exec(say.trim());
  if (!m) return [];
  const first = inAnswerForm(m[1], answer);
  const second = inAnswerForm(m[2], answer);
  const a = answer.trim().toLowerCase();
  if (first.toLowerCase() === second.toLowerCase()) return [];
  if (first.toLowerCase() !== a && second.toLowerCase() !== a) return [];
  return [first, second];
}

function pickRound(config: TileMatchConfig, lastAnswer?: string): Round {
  const items = config.items;
  // Try not to repeat the same item back-to-back when we have a choice.
  let pool = items;
  if (lastAnswer && items.length > 1) {
    pool = items.filter((it) => it.answer !== lastAnswer);
    if (pool.length === 0) pool = items;
  }
  const target = pool[Math.floor(Math.random() * pool.length)];
  const named = namedOptions(target.say, target.answer);
  if (named.length > 0) return { say: target.say, answer: target.answer.trim(), tiles: shuffle(named) };
  const others = [...new Set(items.filter((it) => it.answer !== target.answer).map((it) => it.answer))];
  const want = Math.max(0, Math.min(config.tileCount - 1, others.length));
  const distractors = shuffle(others).slice(0, want);
  return { say: target.say, answer: target.answer, tiles: shuffle([target.answer, ...distractors]) };
}

export default function TileMatch({ config }: { config: TileMatchConfig }) {
  const { settings } = useSettings();
  const [round, setRound] = useState<Round>(() => pickRound(config));
  const [correctTile, setCorrectTile] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const playPromptRef = useRef<() => void>(() => {});

  const playPrompt = useCallback(() => {
    if (config.promptMode === 'AUDIO' && canSpeak()) {
      speak(round.say, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
    }
  }, [config.promptMode, round.say, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => { playPromptRef.current = playPrompt; }, [playPrompt]);

  useEffect(() => {
    const t = window.setTimeout(() => playPromptRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [round]);

  useEffect(() => {
    if (completed) return;
    const seconds = Math.max(PROMPT_REPEAT_MIN_SECONDS, settings.promptRepeatSeconds);
    const id = window.setInterval(() => playPromptRef.current(), seconds * 1000);
    return () => window.clearInterval(id);
  }, [round, completed, settings.promptRepeatSeconds]);

  useEffect(() => {
    if (!completed) return;
    const transitionMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
    const fadeStart = window.setTimeout(() => setFadingOut(true), FEEDBACK_HOLD_MS);
    const advance = window.setTimeout(() => {
      stopSpeaking();
      setRound((prev) => pickRound(config, prev.answer));
      setCorrectTile(null);
      setCompleted(false);
      setFadingOut(false);
    }, FEEDBACK_HOLD_MS + transitionMs);
    return () => {
      window.clearTimeout(fadeStart);
      window.clearTimeout(advance);
    };
  }, [completed, config, settings.roundTransitionMs, settings.roundTransitionType]);

  useEffect(() => () => stopSpeaking(), []);

  function pickTile(tile: string) {
    if (completed) return;
    if (tile === round.answer) {
      setCorrectTile(tile);
      setCompleted(true);
      if (settings.correctPingEnabled) playCorrectPing();
    } else {
      playPrompt();
    }
  }

  const tilesGridClass = useMemo(() => gridClass(round.tiles.length), [round.tiles.length]);
  const effectiveMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
  const transitionStyle = { '--round-transition-ms': `${effectiveMs}ms` } as React.CSSProperties;
  const typeClass = `round-${settings.roundTransitionType.toLowerCase()}`;

  return (
    <>
      {config.promptMode === 'TEXT' && (
        <div className="concept-prompt-text" aria-live="polite">{round.say}</div>
      )}
      <div
        className={`tile-grid ${tilesGridClass} ${typeClass}${fadingOut ? ' fading-out' : ''}`}
        style={transitionStyle}
        role="group"
        aria-label="Tiles"
      >
        {round.tiles.map((tile, i) => {
          const isCorrect = completed && correctTile === tile;
          return (
            <button
              key={`${tile}-${i}`}
              className={`tile${isCorrect ? ' correct' : ''}`}
              onClick={() => pickTile(tile)}
              disabled={completed}
              aria-label={`Tile: ${tile}`}
            >
              {tile}
            </button>
          );
        })}
      </div>
    </>
  );
}
